import React from 'react';
import { Box, ChipGroup, Chip, Heading, Text } from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';

import type { AttendanceSettingsFormState } from 'components/Settings/AttendanceAndLeavesSettings/types';
import { SATURDAY_CHIPS } from 'components/Settings/AttendanceAndLeavesSettings/constants';

export const WeeklyOff = () => {
  const { formData, setFieldValue } = useFormWithProvider<AttendanceSettingsFormState>();

  const allSaturdaysWeekend =
    formData.weekendSat1 &&
    formData.weekendSat2 &&
    formData.weekendSat3 &&
    formData.weekendSat4 &&
    formData.weekendSat5;

  const getSaturdayChipValues = () => {
    const saturdayValues: string[] = [];
    if (allSaturdaysWeekend) saturdayValues.push('all');
    if (formData.weekendSat1) saturdayValues.push('weekendSat1');
    if (formData.weekendSat2) saturdayValues.push('weekendSat2');
    if (formData.weekendSat3) saturdayValues.push('weekendSat3');
    if (formData.weekendSat4) saturdayValues.push('weekendSat4');
    if (formData.weekendSat5) saturdayValues.push('weekendSat5');
    return saturdayValues;
  };

  const handleSaturdayChange = (values: string[]) => {
    const hadAllSaturdaysWeekend = allSaturdaysWeekend;
    const nowHasAllSaturdaysWeekend = values.includes('all');

    const isAllSatWeekendSelected = nowHasAllSaturdaysWeekend && !hadAllSaturdaysWeekend;
    const isAllSatWeekendDeselected = hadAllSaturdaysWeekend && !nowHasAllSaturdaysWeekend;

    if (isAllSatWeekendSelected) {
      setFieldValue('weekendSat1', true);
      setFieldValue('weekendSat2', true);
      setFieldValue('weekendSat3', true);
      setFieldValue('weekendSat4', true);
      setFieldValue('weekendSat5', true);
      return;
    }

    if (isAllSatWeekendDeselected) {
      setFieldValue('weekendSat1', false);
      setFieldValue('weekendSat2', false);
      setFieldValue('weekendSat3', false);
      setFieldValue('weekendSat4', false);
      setFieldValue('weekendSat5', false);
      return;
    }

    // Individual Saturday chips toggled
    for (const chip of SATURDAY_CHIPS) {
      setFieldValue(
        chip.field as keyof AttendanceSettingsFormState,
        values.includes(chip.field) as any,
      );
    }
  };

  return (
    <Box
      padding="spacing.6"
      borderRadius="medium"
      borderWidth="thin"
      borderColor="surface.border.gray.subtle"
      backgroundColor="surface.background.gray.intense">
      <Heading size="medium">Weekend</Heading>
      <Text marginTop="spacing.2" size="medium" color="surface.text.gray.muted">
        Days that don&apos;t count as working days. Salary, loss-of-pay and holidays use this
        pattern.
      </Text>

      <Box marginTop="spacing.6">
        <Text size="medium" color="surface.text.gray.muted">
          Sundays
        </Text>
        <Box marginTop="spacing.3">
          <ChipGroup
            accessibilityLabel="Sunday off"
            selectionType="multiple"
            value={formData.weekendSun ? ['all-sundays'] : []}
            onChange={({ values }) => {
              setFieldValue('weekendSun', values.includes('all-sundays'));
            }}>
            <Chip value="all-sundays">All Sundays</Chip>
          </ChipGroup>
        </Box>
      </Box>

      <Box marginTop="spacing.6">
        <Text size="medium" color="surface.text.gray.muted">
          Saturday cadence, which Saturdays are off?
        </Text>
        <Box marginTop="spacing.3">
          <ChipGroup
            accessibilityLabel="Saturday cadence"
            selectionType="multiple"
            value={getSaturdayChipValues()}
            onChange={({ values }) => handleSaturdayChange(values)}>
            <Chip value="all">All Sat</Chip>
            {SATURDAY_CHIPS.map((chip) => (
              <Chip key={chip.field} value={chip.field}>
                {chip.label}
              </Chip>
            ))}
          </ChipGroup>
        </Box>
      </Box>
    </Box>
  );
};
